/**
 * Brand fonts for the embed widget.
 *
 * `inherit` takes the host website's own font (CSS inheritance crosses the
 * Shadow DOM boundary), `system` is the OS UI font, and the rest are Google
 * Fonts loaded on demand with one <link> in the host document's <head> —
 * @font-face rules declared in the document are usable inside shadow roots.
 */

export interface WidgetFontOption {
  id: string;
  name: string;
  /** CSS font-family value */
  stack: string;
  /** Google Fonts family query (`family=` value), when the font must be loaded */
  google?: string;
}

const SANS_FALLBACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const WIDGET_FONT_OPTIONS: WidgetFontOption[] = [
  { id: 'inherit', name: 'Match my website', stack: 'inherit' },
  { id: 'system', name: 'System', stack: SANS_FALLBACK },
  { id: 'inter', name: 'Inter', stack: `"Inter", ${SANS_FALLBACK}`, google: 'Inter:wght@400;500;600;700' },
  {
    id: 'dm-sans',
    name: 'DM Sans',
    stack: `"DM Sans", ${SANS_FALLBACK}`,
    google: 'DM+Sans:wght@400;500;700',
  },
  { id: 'roboto', name: 'Roboto', stack: `"Roboto", ${SANS_FALLBACK}`, google: 'Roboto:wght@400;500;700' },
  {
    id: 'open-sans',
    name: 'Open Sans',
    stack: `"Open Sans", ${SANS_FALLBACK}`,
    google: 'Open+Sans:wght@400;500;600;700',
  },
  { id: 'lato', name: 'Lato', stack: `"Lato", ${SANS_FALLBACK}`, google: 'Lato:wght@400;700' },
  {
    id: 'poppins',
    name: 'Poppins',
    stack: `"Poppins", ${SANS_FALLBACK}`,
    google: 'Poppins:wght@400;500;600;700',
  },
  {
    id: 'montserrat',
    name: 'Montserrat',
    stack: `"Montserrat", ${SANS_FALLBACK}`,
    google: 'Montserrat:wght@400;500;600;700',
  },
  {
    id: 'nunito',
    name: 'Nunito',
    stack: `"Nunito", ${SANS_FALLBACK}`,
    google: 'Nunito:wght@400;600;700',
  },
  {
    id: 'merriweather',
    name: 'Merriweather',
    stack: '"Merriweather", Georgia, "Times New Roman", serif',
    google: 'Merriweather:wght@400;700',
  },
  {
    id: 'playfair',
    name: 'Playfair Display',
    stack: '"Playfair Display", Georgia, "Times New Roman", serif',
    google: 'Playfair+Display:wght@400;600;700',
  },
];

export function getWidgetFont(id: string | null | undefined): WidgetFontOption | undefined {
  if (!id) return undefined;
  return WIDGET_FONT_OPTIONS.find((f) => f.id === id);
}

/** Adds the Google Fonts stylesheet for `id` to the document once. No-op for inherit/system. */
export function ensureWidgetFontLoaded(id: string | null | undefined): void {
  const font = getWidgetFont(id);
  if (!font?.google || typeof document === 'undefined') return;
  const linkId = `bolder-widget-font-${font.id}`;
  if (document.getElementById(linkId)) return;
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`;
  document.head.appendChild(link);
}
